import { Controller } from "@hotwired/stimulus";

// Two-step submit for the A2P campaign registration form. Intercepts the
// initial submit, mirrors the editable fields (description, samples, opt-in
// flow) into a read-only preview modal alongside the fixed values, and only
// submits for real once the user confirms.
//
// Usage:
//   <form data-controller="campaign-confirm"
//         data-action="submit->campaign-confirm#review">
//     ...editable fields...
//     <textarea name="description" data-campaign-confirm-target="description">
//     <textarea name="message_samples[]" ...>   (all matched by name)
//     <textarea name="opt_in_flow" data-campaign-confirm-target="optInFlow">
//     <div data-campaign-confirm-target="modal" class="hidden"> ...preview...
//       <span data-campaign-confirm-target="previewDescription"></span>
//       <ul data-campaign-confirm-target="previewSamples"></ul>
//       <span data-campaign-confirm-target="previewOptInFlow"></span>
//     </div>
export default class extends Controller {
  static targets = [
    "modal",
    "previewDescription",
    "previewSamples",
    "previewOptInFlow",
    "compliancePrivacy",
    "complianceTerms",
  ];

  // Opt-in screenshot URLs (server-side, same ones submitted to Twilio) and the
  // hosted SMS terms page used when the terms field is left blank.
  static values = { screenshotUrls: Array, hostedTermsUrl: String };

  // First submit: build the preview and open the modal instead of submitting.
  review(event) {
    if (this.confirmed) return; // let the real submit through
    event.preventDefault();

    if (!this.element.reportValidity()) return; // surface native field errors first

    // The compliance URLs, resolved the same way the server will submit them
    // (terms falls back to the hosted SMS terms page when the field is blank).
    const privacy = this.fieldValue("privacy_policy_url");
    const terms =
      this.fieldValue("terms_and_conditions_url") ||
      (this.hasHostedTermsUrlValue ? this.hostedTermsUrlValue : "");

    // Show the FINAL text exactly as submitted: the base copy with the consent
    // block (screenshot links + privacy + terms) appended to both descriptions.
    const block = this.consentBlock(privacy, terms);
    const withBlock = (text) => (block ? `${text}\n\n${block}` : text);

    this.previewDescriptionTarget.textContent =
      withBlock(this.fieldValue("description")) || "—";
    this.previewOptInFlowTarget.textContent =
      withBlock(this.fieldValue("opt_in_flow")) || "—";

    this.previewSamplesTarget.innerHTML = "";
    this.sampleValues().forEach((sample) => {
      const li = document.createElement("li");
      li.textContent = sample;
      this.previewSamplesTarget.appendChild(li);
    });

    if (this.hasCompliancePrivacyTarget) {
      this.compliancePrivacyTarget.textContent = privacy || "— add it in the form above —";
    }
    if (this.hasComplianceTermsTarget) {
      this.complianceTermsTarget.textContent = terms || "—";
    }

    this.open();
  }

  // Mirrors Twilio::RegisterCampaign.consent_block exactly, so the preview
  // matches what actually gets appended to the descriptions on submit.
  consentBlock(privacy, terms) {
    const urls = (this.hasScreenshotUrlsValue ? this.screenshotUrlsValue : []).filter(Boolean);
    const optInUrl = this.fieldValue("opt_in_url");
    const parts = [];
    if (optInUrl) {
      parts.push(`Opt-in page (where members consent): ${optInUrl}`);
    }
    if (urls.length) {
      parts.push(["Screenshot of our opt-in form showing the consent checkboxes:", ...urls].join("\n"));
    }
    if (privacy) {
      parts.push(`Privacy policy (states mobile numbers are not shared with third parties): ${privacy}`);
    }
    if (terms) {
      parts.push(`Terms & conditions: ${terms}`);
    }
    return parts.join("\n\n");
  }

  // Confirm button: submit the form for real.
  confirm() {
    this.confirmed = true;
    this.close();
    this.element.requestSubmit();
  }

  open() {
    this.modalTarget.classList.remove("hidden");
    document.body.style.overflow = "hidden";
  }

  close(event) {
    if (event) event.preventDefault();
    this.modalTarget.classList.add("hidden");
    document.body.style.overflow = "";
  }

  closeOnBackdrop(event) {
    if (event.target === this.modalTarget) this.close(event);
  }

  closeOnEscape(event) {
    if (event.key === "Escape" && !this.modalTarget.classList.contains("hidden")) {
      this.close(event);
    }
  }

  fieldValue(name) {
    const el = this.element.querySelector(`[name="${name}"]`);
    return el ? el.value.trim() : "";
  }

  sampleValues() {
    return Array.from(
      this.element.querySelectorAll('[name="message_samples[]"]'),
    )
      .map((el) => el.value.trim())
      .filter(Boolean);
  }
}
